import Axios from 'axios';
import CONFIG from '../../config';
import NetworkError from './error';

/**
 * Axios instance to comunicate with local PHP backend
 */
const APIClient = Axios.create({
    baseURL: CONFIG.BASE_API_URL,
    withCredentials: true
})

const URLS = {
    GET_LIBRARY: '/library',
    ADD_LIBRARY: '/library/new',
    ADD_MULTIPLE_LIBRARY: '/library/new/multi',
    EDIT_LIBRARY: '/library',
    GET_LIBRARIES: '/libraries',
    TASK_403: '/task-403'
}

type GetConfigResponse = {
    serverTime: number,
    isAdmin: boolean
}

function getConfig(): Promise<GetConfigResponse> {
    return APIClient.get('/config')
        .then((response) => {
            return {
                serverTime: response.data.time,
                isAdmin: response.data.isAdmin || false
            }
        });
}

export type GetLibraryResponse = {
    code: string,
    intro: string,
    outro: string,
    email: string,
    address: string,
    imageId: string,
    imageUrl: string,
    imageAlt: string
}

function getLibrary(data: { code: string }): Promise<GetLibraryResponse> {
    return APIClient.get(URLS.GET_LIBRARY, {
        params: data
    })
        .then(response => {
            if (response.data.status === 'ok') {
                return {
                    code: response.data.payload.code,
                    intro: response.data.payload.intro,
                    outro: response.data.payload.outro,
                    email: response.data.payload.email,
                    address: response.data.payload.address,
                    imageId: response.data.payload.imageId,
                    imageUrl: response.data.payload.imageUrl,
                    imageAlt: response.data.payload.imageAlt
                }
            }

            throw new NetworkError(response.data.errorCode, response.data.errorMessage);
        });
}

export type TaskData = {
    id: string,
    mapNumber: string,
    title: string,
    requires?: string
}

type GetGameResponse = {
    tasks: TaskData[]
}

function getGame(): Promise<GetGameResponse> {
    return Axios.get('/data.json')
        .then(response => {
            return {
                tasks: response.data.tasks || [],
                paths: response.data.paths || []
            }
        })
}

function addLibrary(data: { code: string }) {
    return APIClient.post(URLS.ADD_LIBRARY, {
        code: data.code
    })
}

function addMultipleLibrary(data: { codes: string[] }) {
    return APIClient.post(URLS.ADD_MULTIPLE_LIBRARY, {
        codes: data.codes
    })
}

function editLibrary(data: any) {
    return APIClient.post(URLS.EDIT_LIBRARY, data, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
        .then(response => {
            if (response.data.status === 'error') {
                throw new NetworkError(response.data.errorCode, response.data.errorMessage);
            }
        })
}

function sendTask403(data: {
    libCode: string,
    answers: string[],
}) {
    return APIClient.post(URLS.TASK_403, data);
};

export type GetLibrariesResponse = {
    code: string,
    updated_at: string
}

function getLibraries(): Promise<GetLibrariesResponse[]> {
    return APIClient.get(URLS.GET_LIBRARIES)
        .then(response => {
            return response.data
        })
}

function adminLogin(data: {password: string}) {
    return APIClient.post('/admin/login', data);
}

function adminLogout() {
    return APIClient.post('/admin/logout');
}

function closePublicEdit() {
    return APIClient.post('/admin/public_edit/close');
}

function openPublicEdit() {
    return APIClient.post('/admin/public_edit/open');
}

function getPublicEditStatus() {
    return APIClient.get('/admin/public_edit')
        .then(response => {
            return response.data
        });
}

const Network = {
    getConfig,
    getLibrary,
    addLibrary,
    editLibrary,
    getLibraries,
    getGame,
    sendTask403,

    adminLogin,
    adminLogout,

    closePublicEdit,
    openPublicEdit,
    getPublicEditStatus,
    addMultipleLibrary
}

export default Network;
